// @/src/core/store/configuracion.store.ts

import type { Unsubscribe } from 'firebase/firestore';
import type { Categoria, MetodoPago, EmpresaMensajeria, MetodoIngreso, ProductoConfig, IngresoInventario } from '@/core/types';
import * as ConfigService from '@/core/services/configuracion.service';
import { getErrorMessage } from '@shared/utils/errores';

export interface ConfigState {
        configProductosData: ProductoConfig[];
        ingresosInventarioData: IngresoInventario[];
        customCategoriasEgresosData: Categoria[];
        customMetodosPagoEgresosData: MetodoPago[];
        customCategoriasGastosPersonalesData: Categoria[];
        customMetodosPagoGastosPersonalesData: MetodoPago[];
        empresasMensajeriaData: EmpresaMensajeria[];
        metodosIngresoOtrosData: MetodoIngreso[];
        isLoading: boolean;
        error: string | null;
        isInitialized: boolean;
}

function createConfiguracionStore() {
    let _userId: string | null = null;
    const listeners: Set<(state: ConfigState) => void> = new Set();

    let state: ConfigState = {
        configProductosData: [],
        ingresosInventarioData: [],
        customCategoriasEgresosData: [],
        customMetodosPagoEgresosData: [],
        customCategoriasGastosPersonalesData: [],
        customMetodosPagoGastosPersonalesData: [],
        empresasMensajeriaData: [],
        metodosIngresoOtrosData: [],
        isLoading: true,
        error: null,
        isInitialized: false,
    };

    const notify = () => {
        listeners.forEach(listener => listener(state));
    };

    const subscribe = (listener: (state: ConfigState) => void): Unsubscribe => {
        listeners.add(listener);
        listener(state);
        return () => listeners.delete(listener);
    };

    const init = async (userId: string) => {
        // Si ya está inicializado para el mismo usuario, no hacer nada.
        if (state.isInitialized && _userId === userId && !state.error) {
            console.log('[ConfigStore] ✅ Store ya inicializado para este usuario');
            return;
        }

        if (!userId) {
            state = { ...state, error: 'User ID is required', isLoading: false };
            notify();
            return;
        }

        console.log(`[ConfigStore] 🔄 Inicializando store para usuario: ${userId}`);
        _userId = userId;
        state = { ...state, isLoading: true, error: null };
        notify();

        try {
            // Asegura que los datos por defecto existan si es la primera vez.
            await ConfigService.initializeConfigurationDataIfNeeded(userId);
            
            // Obtiene los datos una sola vez.
            const data = await ConfigService.getConfigurationData(userId);

            console.log(`[ConfigStore] ✅ Datos de configuración cargados - Productos: ${data?.configProductosData?.length || 0}, Ingresos: ${data?.ingresosInventarioData?.length || 0}`);

            state = {
                ...state,
                ...(data || {}),
                isLoading: false,
                isInitialized: true,
                error: null,
            };
            notify();

        } catch (err) {
            console.error('[ConfigStore] Error al inicializar:', err);
            state = { ...state, isLoading: false, error: getErrorMessage(err) || 'Error desconocido' };
            notify();
        }
    };

    /**
     * Fuerza la recarga de datos desde Firestore.
     * Útil cuando sabemos que los datos han cambiado.
     */
    const reload = async () => {
        if (!_userId) {
            console.warn('[ConfigStore] No se puede recargar sin userId');
            return;
        }

        console.log('[ConfigStore] 🔄 Recargando datos desde Firestore...');
        state = { ...state, isLoading: true, error: null };
        notify();

        try {
            const data = await ConfigService.getConfigurationData(_userId);

            console.log(`[ConfigStore] ✅ Datos recargados - Productos: ${data?.configProductosData?.length || 0}, Ingresos: ${data?.ingresosInventarioData?.length || 0}`);

            state = {
                ...state,
                ...(data || {}),
                isLoading: false,
                error: null,
            };
            notify();

        } catch (err) {
            console.error('[ConfigStore] Error al recargar:', err);
            state = { ...state, isLoading: false, error: getErrorMessage(err) || 'Error al recargar' };
            notify();
        }
    };

    const destroy = () => {
        console.log('[ConfigStore] 🔄 Limpiando store');
        _userId = null;
        state = {
            configProductosData: [],
            ingresosInventarioData: [],
            customCategoriasEgresosData: [],
            customMetodosPagoEgresosData: [],
            customCategoriasGastosPersonalesData: [],
            customMetodosPagoGastosPersonalesData: [],
            empresasMensajeriaData: [],
            metodosIngresoOtrosData: [],
            isLoading: true,
            error: null,
            isInitialized: false,
        };
        listeners.clear();
    };

    return {
        subscribe,
        init,
        reload,
        destroy,
        getState: () => state,
    };
}

export const configuracionStore = createConfiguracionStore();
